import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Wrench, Package, Trash2, Save, X, Tag } from "lucide-react";

const KIND_LABEL = { mao_de_obra: "Mão de obra", peca: "Peça", outro: "Outro" };
const money = (v) => ("R$ " + Number(v || 0).toFixed(2)).replace(".", ",");
const EMPTY = { name: "", value: "", kind: "mao_de_obra" };

export default function ServiceCatalogTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/services"); setItems(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha ao carregar serviços"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Informe o nome do serviço."); return; }
    const body = { name: form.name.trim(), value: Number(form.value) || 0, kind: form.kind };
    try {
      if (editingId) await api.patch(`/services/${editingId}`, body);
      else await api.post("/services", body);
      toast.success(editingId ? "Serviço atualizado." : "Serviço cadastrado.");
      reset(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const startEdit = (s) => { setEditingId(s.id); setForm({ name: s.name, value: s.value, kind: s.kind || "mao_de_obra" }); };

  const remove = async () => {
    try { await api.delete(`/services/${toDelete.id}`); toast.success("Serviço removido."); setToDelete(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Cadastre serviços e valores para reutilizar ao montar orçamentos.</p>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end" data-testid="service-form">
            <div className="sm:col-span-5 space-y-1.5">
              <Label className="text-xs">Serviço</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex: Instalação Split 12000" data-testid="service-name-input" />
            </div>
            <div className="sm:col-span-3 space-y-1.5">
              <Label className="text-xs">Tipo</Label>
              <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
                <SelectTrigger data-testid="service-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mao_de_obra">Mão de obra</SelectItem>
                  <SelectItem value="peca">Peça</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs">Valor (R$)</Label>
              <Input type="number" min="0" step="0.01" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} placeholder="0,00" data-testid="service-value-input" />
            </div>
            <div className="sm:col-span-2 flex gap-1.5">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="service-save-button">
                {editingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {editingId ? "Salvar" : "Add"}
              </Button>
              {editingId && <Button type="button" variant="ghost" className="px-2" onClick={reset} data-testid="service-cancel-edit"><X className="h-4 w-4" /></Button>}
            </div>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12" data-testid="services-empty">
          <Tag className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nenhum serviço cadastrado ainda.</p>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="services-list">
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid={`service-${s.id}`}>
              <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                {s.kind === "peca" ? <Package className="h-4 w-4 text-[#1e7be0]" /> : <Wrench className="h-4 w-4 text-[#1e7be0]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">{s.name}</p>
                <p className="text-xs text-slate-500">{KIND_LABEL[s.kind] || s.kind} · <b>{money(s.value)}</b></p>
              </div>
              <Button size="sm" variant="outline" className="h-8" onClick={() => startEdit(s)} data-testid={`service-edit-${s.id}`}>Editar</Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => setToDelete(s)} data-testid={`service-delete-${s.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="service-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover serviço?</AlertDialogTitle>
            <AlertDialogDescription>O serviço <b>{toDelete?.name}</b> será removido do catálogo.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="service-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid="service-delete-confirm-button">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
